import React from 'react';
import { Target, Eye, CheckCircle } from 'lucide-react';
import { companyInfo, certifications } from '../data/mockData';

const About = () => {
  return (
    <section id="about" className="py-20 bg-white">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-amber-400/10 border border-amber-400/20 rounded-full px-4 py-2 mb-4">
            <span className="text-amber-600 font-medium text-sm">About SMC</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Who We Are
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Leading consultancy firm driving sustainable socio-economic development in Nigeria
          </p>
        </div>

        {/* Company Overview */}
        <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-3xl p-8 lg:p-12 mb-16">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            {companyInfo.description}
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            We are well-positioned to contribute to the growth and development of Nigeria's economy, helping to build a more prosperous future for all Nigerians by adding intellectual value to the system of Service. We are committed to helping organizations, businesses, and the government achieve their goals and objectives.
          </p>
        </div>

        {/* Vision & Mission */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {/* Vision */}
          <div className="bg-gradient-to-br from-blue-900 to-slate-900 rounded-3xl p-8 lg:p-10 text-white transform hover:scale-105 transition-transform duration-300">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-yellow-600 rounded-xl flex items-center justify-center mb-6">
              <Eye size={32} className="text-slate-900" />
            </div>
            <h3 className="text-2xl font-bold mb-4">Our Vision</h3>
            <p className="text-gray-300 leading-relaxed text-lg">
              {companyInfo.vision}
            </p>
          </div>

          {/* Mission */}
          <div className="bg-gradient-to-br from-amber-400 to-yellow-600 rounded-3xl p-8 lg:p-10 text-slate-900 transform hover:scale-105 transition-transform duration-300">
            <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center mb-6">
              <Target size={32} className="text-amber-400" />
            </div>
            <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
            <p className="text-slate-800 leading-relaxed text-lg">
              {companyInfo.mission}
            </p>
          </div>
        </div>

        {/* Core Values / Certifications */}
        <div className="bg-slate-50 rounded-3xl p-8 lg:p-12">
          <h3 className="text-3xl font-bold text-slate-900 mb-8 text-center">
            Our Certifications & Accreditations
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {/* PMI - PMP Certified */}
            <div className="bg-white rounded-xl p-6 text-center border-2 border-transparent hover:border-amber-400 transition-all duration-300 transform hover:scale-105 flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_smc-nigeria-pro/artifacts/7zvnsb5h_image.png" 
                alt="PMP Certified" 
                className="w-full h-auto object-contain"
              />
            </div>

            {/* CMC - Certified Management Consultant */}
            <div className="bg-white rounded-xl p-6 text-center border-2 border-transparent hover:border-amber-400 transition-all duration-300 transform hover:scale-105 flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_smc-nigeria-pro/artifacts/t9l352s9_CMC.png" 
                alt="Certified Management Consultant" 
                className="w-full h-auto object-contain"
              />
            </div>

            {/* CMI - Chartered Management Institute */}
            <div className="bg-white rounded-xl p-6 text-center border-2 border-transparent hover:border-amber-400 transition-all duration-300 transform hover:scale-105 flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_smc-nigeria-pro/artifacts/y64j4z1k_CMI.png" 
                alt="Chartered Management Institute" 
                className="w-full h-auto object-contain"
              />
            </div>

            {/* ACCA */}
            <div className="bg-white rounded-xl p-6 text-center border-2 border-transparent hover:border-amber-400 transition-all duration-300 transform hover:scale-105 flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_smc-nigeria-pro/artifacts/ng72e1r4_ACCA.png" 
                alt="ACCA - Association of Chartered Certified Accountants" 
                className="w-full h-auto object-contain"
              />
            </div>

            {/* AMS - Advanced Management Skills */}
            <div className="bg-white rounded-xl p-6 text-center border-2 border-transparent hover:border-amber-400 transition-all duration-300 transform hover:scale-105 flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_smc-nigeria-pro/artifacts/ure66cdp_AMS.jpg" 
                alt="Advanced Management Skills" 
                className="w-full h-auto object-contain"
              />
            </div>

            {/* IBC - Institute of Business Consulting */}
            <div className="bg-white rounded-xl p-6 text-center border-2 border-transparent hover:border-amber-400 transition-all duration-300 transform hover:scale-105 flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_smc-nigeria-pro/artifacts/w6y33oqo_IBC.jpg" 
                alt="Institute of Business Consulting" 
                className="w-full h-auto object-contain"
              />
            </div>

            {/* KPI Institute */}
            <div className="bg-white rounded-xl p-6 text-center border-2 border-transparent hover:border-amber-400 transition-all duration-300 transform hover:scale-105 flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_smc-nigeria-pro/artifacts/idshkamp_KPI.jpg" 
                alt="The KPI Institute" 
                className="w-full h-auto object-contain"
              />
            </div>

            {/* Nigerian Bar Association */}
            <div className="bg-white rounded-xl p-6 text-center border-2 border-transparent hover:border-amber-400 transition-all duration-300 transform hover:scale-105 flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_smc-nigeria-pro/artifacts/c0rpqd0k_NBA.jpg" 
                alt="Nigerian Bar Association" 
                className="w-full h-auto object-contain"
              />
            </div>

            {/* Oracle Certified Professional */}
            <div className="bg-white rounded-xl p-6 text-center border-2 border-transparent hover:border-amber-400 transition-all duration-300 transform hover:scale-105 flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_smc-nigeria-pro/artifacts/s3jzqqtj_ORACLE.jpg" 
                alt="Oracle Certified Professional" 
                className="w-full h-auto object-contain"
              />
            </div>

            {/* IVM - Innovation & Value Management */}
            <div className="bg-white rounded-xl p-6 text-center border-2 border-transparent hover:border-amber-400 transition-all duration-300 transform hover:scale-105 flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_smc-nigeria-pro/artifacts/tcr68kih_IVM.jpg" 
                alt="Innovation & Value Management" 
                className="w-full h-auto object-contain"
              />
            </div>
          </div>
        </div>

        {/* SMC Delivery Promise */}
        <div className="mt-16 bg-gradient-to-r from-slate-900 to-blue-900 rounded-3xl p-8 lg:p-12 text-white">
          <h3 className="text-3xl font-bold mb-6 text-center">SMC Delivery Promise</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="flex items-start space-x-3">
              <CheckCircle className="text-amber-400 flex-shrink-0 mt-1" size={24} />
              <p className="text-gray-300">Valuable expertise and deep industry knowledge</p>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="text-amber-400 flex-shrink-0 mt-1" size={24} />
              <p className="text-gray-300">Research on best global practices</p>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="text-amber-400 flex-shrink-0 mt-1" size={24} />
              <p className="text-gray-300">Innovative data analysis and insights</p>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="text-amber-400 flex-shrink-0 mt-1" size={24} />
              <p className="text-gray-300">Fresh perspectives and recommendations</p>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="text-amber-400 flex-shrink-0 mt-1" size={24} />
              <p className="text-gray-300">Time and resource optimization</p>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircle className="text-amber-400 flex-shrink-0 mt-1" size={24} />
              <p className="text-gray-300">Amazing results and measurable impact</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
